import {useState, FC, useMemo, useEffect, useRef, useContext} from "react";
import "./FollowAllTeams.scss";
import {useFormContext, RegisterOptions, UseFormRegister, FieldValues} from "react-hook-form";
import {ContentProxyTeam} from "../../models/contentProxyModel";
import {User} from "../../models/enums/userModel";
import {CountryCodeEnum} from "../../models/enums/countryCodeEnum";

type Team = Pick<ContentProxyTeam, "code" | "name" | "logo">;

interface FollowAllTeamsProps {
    userData?: User;
    selectedFavouriteTeam?: CountryCodeEnum;
    register: UseFormRegister<FieldValues>,
    options: RegisterOptions,
    teamData: ContentProxyTeam[]
}

interface FollowAllTeamsCheckboxProps {
    team: Team;
}

const normalizeUrl = (url: string) => {
    return url.startsWith("//") ? `https:${url}` : url;
};

const SAILGP_Code = "GEN";

const FollowAllTeams: FC<FollowAllTeamsProps> = ({userData, selectedFavouriteTeam, register, options, teamData}) => {
    const field = {apiField: 'teamInterest'}
    const {setValue} = useFormContext();
    const [selectedTeams, setSelectedTeams] = useState<CountryCodeEnum[]>([]);
    const allTeamCodes = useMemo(() => teamData?.filter((item) => item.code !== SAILGP_Code).map((team) => team.code) ?? [], [teamData]);
    const [isInitialized, setIsInitialized] = useState<boolean>(false)

    const previousFavouriteTeam = useRef<string | null>(null);

    // Initialize from user data once
    useEffect(() => {
        if (!isInitialized) {
            let teamsPrep = [];
            if (userData?.teamInterest) {
                const initialTeams = userData.teamInterest.split(",").map((team: string) => team.trim()).filter(Boolean);
                teamsPrep = [...initialTeams]
                setValue(field.apiField, userData.teamInterest, {shouldValidate: true});
            }
            if (selectedFavouriteTeam && !teamsPrep.includes(selectedFavouriteTeam)) teamsPrep.push(selectedFavouriteTeam)

            setSelectedTeams(teamsPrep)

            setIsInitialized(true);
        }
    }, [userData?.teamInterest, field.apiField, setValue, isInitialized, selectedFavouriteTeam]);

    // Handle favorite team changes
    useEffect(() => {
        if (!isInitialized) return;
        const userFavTeam = selectedFavouriteTeam || '';

        // Only run if the favorite team has actually changed
        if (previousFavouriteTeam.current !== userFavTeam) {
            setSelectedTeams(currentTeams => {
                let newTeams = [...currentTeams];

                // Remove the previous favorite team, if it exists in the list
                if (previousFavouriteTeam.current) {
                    newTeams = newTeams.filter(team => {
                        return team !== previousFavouriteTeam.current
                    });
                }

                // Add the new favorite team to the list, if it's not already there and it not the first render
                if (previousFavouriteTeam.current && userFavTeam && !newTeams.includes(userFavTeam)) {
                    newTeams = [...newTeams, userFavTeam];
                }

                // Update form value
                setValue(field.apiField, newTeams.sort().join(", "), {shouldValidate: true, shouldDirty: true});

                previousFavouriteTeam.current = userFavTeam;
                return newTeams;
            });
        }
    }, [selectedFavouriteTeam, setValue, field.apiField, isInitialized]);

    const onClickFollowAllTeams = () => {
        const isChecked = selectedTeams.length === allTeamCodes.length;
        if (isChecked) {
            setValue(field.apiField, "", {shouldValidate: true, shouldDirty: true});
            setSelectedTeams([]);
        } else {
            const teamsToSelect = [...allTeamCodes];
            setValue(field.apiField, teamsToSelect.sort().join(", "), {shouldValidate: true, shouldDirty: true});
            setSelectedTeams(teamsToSelect);
        }
    };

    const onClickTeamsMultiCheckbox = (team) => {
        const isAlreadyChecked = selectedTeams.includes(team.code);

        const newTeams = isAlreadyChecked
            ? selectedTeams.filter((value: string) => value !== team.code)
            : [...selectedTeams, team.code];

        setSelectedTeams(newTeams);
        setValue(field.apiField, newTeams.sort().join(", "), {shouldValidate: true, shouldDirty: true});
    };

    const FollowAllTeamsCheckbox: FC<FollowAllTeamsCheckboxProps> = () => {
        const isChecked = selectedTeams.length === allTeamCodes.length;

        return (
            <div
                className={`teams-multi-checkbox teams-multi-checkbox--select-all ${
                    isChecked ? "teams-multi-checkbox--checked" : ""
                }`}
                onClick={onClickFollowAllTeams}
            >
                <input
                    type="checkbox"
                    checked={isChecked}
                    readOnly
                    id="followAllTeams"
                    className="teams-multi-checkbox-input"
                />
                <label htmlFor="followAllTeams" onClick={(e) => e.preventDefault()}>
                    <div className="circle-backgraund">
                        <img
                            src={"/SailGP_White_S.png"}
                            width={32}
                            height={21}
                            alt="SailGP icon"
                            className="teams-multi-checkbox-icon"
                        />
                    </div>
                    Follow All Teams
                </label>
            </div>
        );
    };

    const sailGPTeam = teamData.find((item) => item.code === SAILGP_Code);
    const otherTeams = teamData.filter((item) => item.code !== SAILGP_Code);
    const orderedTeams = sailGPTeam ? [sailGPTeam, ...otherTeams] : otherTeams;

    return (
        <div className='follow-teams form-dark-mode w-full'>
            <div className='follow-teams-label'>
                <label className="insightsForm__label insightsForm__label--not-required" htmlFor="followAllTeams">
                    Follow Teams
                </label>
                <p className='follow-teams-label__mini'>
                    By selecting these options you are consenting to receive marketing materials from the selected
                    teams.
                </p>
            </div>
            <div className="teams-multi-checkbox-wrapper">
                {orderedTeams.map((team) => {
                    if (team.code === SAILGP_Code) {
                        return <FollowAllTeamsCheckbox key={team.code} team={team}/>;
                    }
                    const isChecked = selectedTeams.includes(team.code);

                    return (
                        <div
                            key={`${field.apiField}_${team.code}`}
                            className={`teams-multi-checkbox ${isChecked ? "teams-multi-checkbox--checked" : ""}`}
                            onClick={() => onClickTeamsMultiCheckbox(team)}
                        >
                            <input
                                type="checkbox"
                                name={field.apiField}
                                id={`${field.apiField}_${team.code}`}
                                value={team.code}
                                checked={isChecked}
                                readOnly
                                className="teams-multi-checkbox-input"
                                {...register(field.apiField, options)}
                            />
                            <label htmlFor={`${field.apiField}_${team.code}`} onClick={(e) => e.preventDefault()}>
                                <img
                                    src={normalizeUrl(team?.logo?.file.url || '')}
                                    width={32}
                                    height={32}
                                    alt="icon"
                                    className="teams-multi-checkbox-icon"
                                />
                                {team.name}
                            </label>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default FollowAllTeams;